import React from 'react';
import Link from 'next/link';
import cn from 'classnames';
import Loader from '../Loader/Loader';

export type ButtonProps = {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'disabled' | 'link' | 'underline' | 'alert';
  buttonBorderColor?: string;
  bgColor?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
};

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  buttonBorderColor,
  bgColor,
  onClick,
  href,
  type = 'button',
  loading,
  fullWidth = true,
  disabled,
  className,
}) => {
  const isDisabled = disabled || variant === 'disabled';
  const buttonClass = cn(
    'whitespace-nowrap text-lg cursor-pointer inline-block px-8 py-4 rounded-lg text-center leading-none tracking-none transition-colors duration-200 box-border border-border',
    {
      'bg-black text-white hover:bg-white hover:text-black transition-colors duration-300':
        variant === 'primary' && !isDisabled,
      'bg-white text-black hover:bg-black hover:text-white transition-colors duration-300 border-black border-2':
        variant === 'secondary' && !bgColor && !isDisabled,
      'bg-red-500 text-black hover:bg-red-900 hover:text-white transition-colors duration-300 border-black border-2':
        variant === 'alert' && !bgColor && !isDisabled,
      'bg-gray-500 text-white cursor-not-allowed opacity-60 pointer-events-none':
        isDisabled && !bgColor && variant !== 'link' && variant !== 'underline',
      'text-black !underline !text-left !p-0 !w-auto': variant === 'link' && !bgColor,
      'text-black !underline !text-left !p-0 !w-auto hover:no-underline':
        variant === 'underline' && !bgColor,
      'text-gray-400 cursor-not-allowed pointer-events-none':
        (variant === 'link' || variant === 'underline') && isDisabled,
      [`border ${buttonBorderColor}`]: buttonBorderColor,
      [`${bgColor}`]: bgColor,
      'w-full': fullWidth,
    },
    className,
  );

  const isInternalLink = href && href.startsWith('/');

  if (isInternalLink && !isDisabled) {
    return (
      <Link href={href} className={buttonClass}>
        {loading ? <Loader position={'absolute'} /> : children}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={isDisabled ? undefined : href}
        onClick={(e) => {
          if (isDisabled) e.preventDefault();
        }}
        className={buttonClass}
      >
        {loading ? <Loader position={'absolute'} /> : children}
      </a>
    );
  }

  return (
    <button
      className={buttonClass}
      onClick={isDisabled ? undefined : onClick}
      type={type}
      disabled={isDisabled}
    >
      {loading ? <Loader position={'absolute'} /> : children}
    </button>
  );
};

export default Button;
